import React, { Fragment, useState, useEffect } from "react";
import {
    Row,
    Col,
    Card,
    Form,
    Button,
    Alert,
    Spinner,
    Badge,
    Table,
} from "react-bootstrap";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PageTitle from "../../layouts/PageTitle";
import api from "../../../api";
import swal from "sweetalert";
import WirDocumentPreview from "./WirDocumentPreview";

const WirAdd = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submittingAction, setSubmittingAction] = useState("");

    const [workOrders, setWorkOrders] = useState([]);
    const [projects, setProjects] = useState([]);
    const [jmrs, setJmrs] = useState([]);
    const [mirs, setMirs] = useState([]);
    const [drawingDocuments, setDrawingDocuments] = useState([]);

    const [selectedJmrId, setSelectedJmrId] = useState("");
    const [selectedJmrs, setSelectedJmrs] = useState([]);
    const [selectedMirId, setSelectedMirId] = useState("");
    const [selectedMirs, setSelectedMirs] = useState([]);
    const [selectedDrawingDocId, setSelectedDrawingDocId] = useState("");
    const [selectedDrawingDocs, setSelectedDrawingDocs] = useState([]);
    const [supportingDocs, setSupportingDocs] = useState([]);
    const signatureDocTypes = [
        "inspectorSignature",
        "inspectorStamp",
        "clientSignature",
        "clientStamp",
    ];

    const [formData, setFormData] = useState({
        workOrder: searchParams.get("workOrder") || "",
        project: "",
        location: "",
        activityDescription: "",
        drawingReferences: "",
        inspectionDate: "",
        JmrReferences: [],
        inspectionRemarks: "",
        remarks: "",
        approvalStatus: "Draft",
        // New fields from Excel format
        correctiveAction: "",
        reinspectionRequired: false,
        reinspectionDate: "",
        newWirNo: "",
        inspectorName: "",
        inspectorDesignation: "",
        inspectorOrganisation: "",
        inspectorDate: "",
        clientDecision: "",
        clientRepresentativeName: "",
        clientDesignation: "",
        clientDate: "",
    });

    useEffect(() => {
        fetchDropdowns();
    }, []);

    useEffect(() => {
        if (!formData.workOrder) return;

        const selectedWorkOrder = workOrders.find(
            (wo) => wo._id === formData.workOrder
        );

        if (selectedWorkOrder?.project) {
            const projectId =
                selectedWorkOrder.project?._id || selectedWorkOrder.project;

            setFormData((prev) => ({
                ...prev,
                project: projectId,
            }));
        }

        fetchJmrs(formData.workOrder);
        fetchMirs(formData.workOrder);
        fetchDrawingDocuments(formData.workOrder);
    }, [formData.workOrder, workOrders]);

    const fetchDropdowns = async () => {
        try {
            setLoading(true);

            const [woRes, prRes] = await Promise.all([
                api.get("/work-orders?limit=1000"),
                api.get("/projects?limit=1000"),
            ]);

            if (woRes.data.success) {
                setWorkOrders(woRes.data.data || []);
            }

            if (prRes.data.success) {
                setProjects(prRes.data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch dropdowns", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchJmrs = async (workOrderId) => {
        try {
            if (!workOrderId) return;

            const res = await api.get(`/wir/jmrs/${workOrderId}`, {
                params: { limit: 1000 },
            });

            if (res.data.success) {
                setJmrs(res.data.data || []);
            }

            console.log("JMRs fetched:", res.data);
        } catch (error) {
            console.error("Failed to fetch JMRs", error);
            setJmrs([]);
        }
    };

    const fetchMirs = async (workOrderId) => {
        try {
            if (!workOrderId) return;

            const res = await api.get(`/wir/mirs/${workOrderId}`, {
                params: { limit: 1000 },
            });

            if (res.data.success) {
                setMirs(res.data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch MIRs", error);
            setMirs([]);
        }
    };

    const fetchDrawingDocuments = async (workOrderId) => {
        try {
            if (!workOrderId) return;

            const res = await api.get(`/wir/drawing-documents/${workOrderId}`);

            if (res.data.success) {
                setDrawingDocuments(res.data.data || []);
            }
        } catch (error) {
            console.error("Failed to fetch approved drawing documents", error);
            setDrawingDocuments([]);
        }
    };

        const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => {
            const next = { ...prev, [name]: value };
            if (name === "project") {
                if (value && prev.workOrder) {
                    const currentWo = workOrders.find((w) => String(w._id) === String(prev.workOrder));
                    const currentWoProjId = currentWo?.project?._id || currentWo?.project;
                    if (currentWoProjId && String(currentWoProjId) !== String(value)) {
                        next.workOrder = "";
                    }
                }
            } else if (name === "workOrder") {
                if (value) {
                    const selectedWo = workOrders.find((w) => String(w._id) === String(value));
                    const autoProjId = selectedWo?.project?._id || selectedWo?.project;
                    if (autoProjId) {
                        next.project = String(autoProjId);
                    }
                }
            }
            return next;
        });

        if (name === "workOrder") {
            setSelectedJmrId("");
            setSelectedJmrs([]);
            setSelectedMirId("");
            setSelectedMirs([]);
            setSelectedDrawingDocId("");
            setSelectedDrawingDocs([]);
            setJmrs([]);
            setMirs([]);
            setDrawingDocuments([]);
            setFormData((prev) => ({
                ...prev,
                drawingReferences: "",
            }));
        }
    };

    const handleAddDrawingDoc = () => {
        if (!selectedDrawingDocId) return;

        const selectedDoc = drawingDocuments.find((item) => item._id === selectedDrawingDocId);
        if (!selectedDoc) return;

        const alreadyAdded = selectedDrawingDocs.some((item) => item._id === selectedDrawingDocId);
        if (alreadyAdded) {
            swal("Already Added", "This drawing document is already selected", "warning");
            return;
        }

        const nextDocs = [...selectedDrawingDocs, selectedDoc];
        setSelectedDrawingDocs(nextDocs);
        setFormData((prev) => ({
            ...prev,
            drawingReferences: nextDocs.map((doc) => doc.drawingReference || doc.fileName).filter(Boolean).join(", "),
        }));
        setSelectedDrawingDocId("");
    };

    const handleRemoveDrawingDoc = (docId) => {
        const nextDocs = selectedDrawingDocs.filter((item) => item._id !== docId);
        setSelectedDrawingDocs(nextDocs);
        setFormData((prev) => ({
            ...prev,
            drawingReferences: nextDocs.map((doc) => doc.drawingReference || doc.fileName).filter(Boolean).join(", "),
        }));
    };

    const handleAddJmr = () => {
        if (!selectedJmrId) return;

        const selectedJmr = jmrs.find((item) => item._id === selectedJmrId);

        if (!selectedJmr) return;

        const alreadyAdded = selectedJmrs.some(
            (item) => item.jmrId === selectedJmrId
        );

        if (alreadyAdded) {
            swal("Already Added", "This JMR is already selected", "warning");
            return;
        }

        setSelectedJmrs((prev) => [
            ...prev,
            {
                jmrId: selectedJmr._id,
                jmrNumber: selectedJmr.jmrNumber,
            },
        ]);

        setSelectedJmrId("");
    };

    const handleRemoveJmr = (jmrId) => {
        setSelectedJmrs((prev) => prev.filter((item) => item.jmrId !== jmrId));
    };

    const handleAddMir = () => {
        if (!selectedMirId) return;

        const selectedMir = mirs.find((item) => item._id === selectedMirId);
        if (!selectedMir) return;

        const alreadyAdded = selectedMirs.some((item) => item.mirId === selectedMirId);
        if (alreadyAdded) {
            swal("Already Added", "This MIR is already selected", "warning");
            return;
        }

        setSelectedMirs((prev) => [
            ...prev,
            {
                mirId: selectedMir._id,
                mirNumber: selectedMir.mirNumber,
            },
        ]);

        setSelectedMirId("");
    };

    const handleRemoveMir = (mirId) => {
        setSelectedMirs((prev) => prev.filter((item) => item.mirId !== mirId));
    };

    const handleFileChange = (e, docType) => {
        const file = e.target.files[0];

        if (!file) return;

        setSupportingDocs((prev) => [
            ...prev,
            {
                documentType: docType,
                file,
            },
        ]);

        e.target.value = "";
    };

    const handleRemoveDoc = (index) => {
        setSupportingDocs((prev) => prev.filter((_, i) => i !== index));
    };

    const renderSelectedDocPreview = (docType) => {
        const docIndex = supportingDocs.findIndex((doc) => doc.documentType === docType);
        if (docIndex === -1) return null;

        return (
            <div className="mt-2">
                <WirDocumentPreview doc={supportingDocs[docIndex]} />
                <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    className="mt-1"
                    onClick={() => handleRemoveDoc(docIndex)}
                >
                    Remove
                </Button>
            </div>
        );
    };

    const handleSubmit = async (status = "Draft") => {
        try {
            setSubmitting(true);
            setSubmittingAction(status);

            const formDataToSend = new FormData();

            Object.keys(formData).forEach((key) => {
                if (key !== "drawingReferences" && key !== "JmrReferences" && key !== "approvalStatus") {
                    formDataToSend.append(key, formData[key]);
                }
            });

            if (formData.drawingReferences) {
                const drawings = formData.drawingReferences
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean);

                formDataToSend.append("drawingReferences", JSON.stringify(drawings));
            } else {
                formDataToSend.append("drawingReferences", JSON.stringify([]));
            }

            if (selectedDrawingDocs.length > 0) {
                formDataToSend.append(
                    "supportingDocuments",
                    JSON.stringify(selectedDrawingDocs.map((doc) => doc.wirDocument))
                );
            }

            formDataToSend.append("JmrReferences", JSON.stringify(selectedJmrs));
            formDataToSend.append("MirReferences", JSON.stringify(selectedMirs));

            // Handle nested inspector details
            formDataToSend.append("inspectorDetails[inspectorName]", formData.inspectorName);
            formDataToSend.append("inspectorDetails[designation]", formData.inspectorDesignation);
            formDataToSend.append("inspectorDetails[organisation]", formData.inspectorOrganisation);
            formDataToSend.append("inspectorDetails[date]", formData.inspectorDate);

            // Handle nested client representative details
            formDataToSend.append("clientRepresentativeDetails[representativeName]", formData.clientRepresentativeName);
            formDataToSend.append("clientRepresentativeDetails[designation]", formData.clientDesignation);
            formDataToSend.append("clientRepresentativeDetails[date]", formData.clientDate);

            supportingDocs.forEach((doc) => {
                if (signatureDocTypes.includes(doc.documentType)) {
                    formDataToSend.append(doc.documentType, doc.file);
                } else {
                    formDataToSend.append(`supportingDoc_${doc.documentType}`, doc.file);
                }
            });

            formDataToSend.append("approvalStatus", status);

            const res = await api.post("/wir", formDataToSend, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            if (res.data.success) {
                swal(
                    status === "Submitted" ? "Submitted!" : "Saved!",
                    `WIR ${status.toLowerCase()} successfully`,
                    "success"
                );

                navigate("/wir");
            }
        } catch (error) {
            swal(
                "Error",
                error.response?.data?.message || "Failed to save WIR",
                "error"
            );
        } finally {
            setSubmitting(false);
            setSubmittingAction("");
        }
    };

    const filteredWorkOrders = formData.project
        ? workOrders.filter((wo) => {
            const projectId = wo.project?._id || wo.project;
            return String(projectId) === String(formData.project);
        })
        : workOrders;

    return (
        <Fragment>
            <PageTitle activeMenu="Add WIR" motherMenu="Quality" />

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header>
                            <Card.Title>Create Work Inspection Request</Card.Title>
                        </Card.Header>

                        <Card.Body>
                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner variant="primary" />
                                </div>
                            ) : (
                                <Form>
                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Label>Project *</Form.Label>
                                            <Form.Select
                                                name="project"
                                                value={formData.project}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Project</option>
                                                {projects.map((project) => (
                                                    <option key={project._id} value={project._id}>
                                                        {project.projectCode
                                                            ? `${project.projectCode} — `
                                                            : ""}
                                                        {project.projectName}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Work Order *</Form.Label>
                                            <Form.Select
                                                name="workOrder"
                                                value={formData.workOrder}
                                                onChange={handleChange}
                                                required
                                            >
                                                <option value="">Select Work Order</option>
                                                {filteredWorkOrders.map((wo) => (
                                                    <option key={wo._id} value={wo._id}>
                                                        {wo.woNo}{wo.label ? ` – ${wo.label}` : ""}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Label>Location/Area *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleChange}
                                                placeholder="Enter location or area"
                                                required
                                            />
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Activity Description *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="activityDescription"
                                                value={formData.activityDescription}
                                                onChange={handleChange}
                                                placeholder="Enter activity description"
                                                required
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Label>Drawing References</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="drawingReferences"
                                                value={formData.drawingReferences}
                                                readOnly
                                                placeholder="Select approved drawing documents"
                                            />
                                            <div className="d-flex gap-2 mt-2">
                                                <Form.Select
                                                    value={selectedDrawingDocId}
                                                    onChange={(e) => setSelectedDrawingDocId(e.target.value)}
                                                    disabled={!formData.workOrder}
                                                >
                                                    <option value="">
                                                        {formData.workOrder
                                                            ? "Select approved drawing document"
                                                            : "Select Work Order First"}
                                                    </option>
                                                    {drawingDocuments.map((doc) => (
                                                        <option key={doc._id} value={doc._id}>
                                                            {doc.fileName} {doc.version ? `(v${doc.version})` : ""}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleAddDrawingDoc}
                                                    disabled={!selectedDrawingDocId}
                                                >
                                                    Add
                                                </Button>
                                            </div>
                                            {selectedDrawingDocs.length > 0 && (
                                                <div className="mt-2">
                                                    {selectedDrawingDocs.map((doc) => (
                                                        <Badge
                                                            key={doc._id}
                                                            bg="success"
                                                            className="me-2 mb-2"
                                                            style={{ fontSize: "14px", padding: "8px 12px" }}
                                                        >
                                                            {doc.fileName}
                                                            <span
                                                                style={{ cursor: "pointer", marginLeft: "8px" }}
                                                                onClick={() => handleRemoveDrawingDoc(doc._id)}
                                                            >
                                                                x
                                                            </span>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Inspection Date *</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="inspectionDate"
                                                value={formData.inspectionDate}
                                                onChange={handleChange}
                                                required
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>JMR References Supporting Evidence</Form.Label>

                                            <div className="d-flex gap-2 mb-2">
                                                <Form.Select
                                                    value={selectedJmrId}
                                                    onChange={(e) => setSelectedJmrId(e.target.value)}
                                                    style={{ flex: 1 }}
                                                    disabled={!formData.workOrder}
                                                >
                                                    <option value="">
                                                        {formData.workOrder
                                                            ? "Select JMR"
                                                            : "Select Work Order First"}
                                                    </option>

                                                    {jmrs.map((jmr) => (
                                                        <option key={jmr._id} value={jmr._id}>
                                                            {jmr.jmrNumber}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleAddJmr}
                                                    disabled={!selectedJmrId}
                                                >
                                                    Add
                                                </Button>
                                            </div>

                                            {selectedJmrs.length > 0 && (
                                                <div className="mt-2">
                                                    {selectedJmrs.map((jmr) => (
                                                        <Badge
                                                            key={jmr.jmrId}
                                                            bg="info"
                                                            className="me-2 mb-2"
                                                            style={{
                                                                fontSize: "14px",
                                                                padding: "8px 12px",
                                                            }}
                                                        >
                                                            {jmr.jmrNumber}
                                                            <span
                                                                style={{
                                                                    cursor: "pointer",
                                                                    marginLeft: "8px",
                                                                }}
                                                                onClick={() => handleRemoveJmr(jmr.jmrId)}
                                                            >
                                                                ×
                                                            </span>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>MIR References Material Evidence</Form.Label>

                                            <div className="d-flex gap-2 mb-2">
                                                <Form.Select
                                                    value={selectedMirId}
                                                    onChange={(e) => setSelectedMirId(e.target.value)}
                                                    style={{ flex: 1 }}
                                                    disabled={!formData.workOrder}
                                                >
                                                    <option value="">
                                                        {formData.workOrder
                                                            ? "Select MIR"
                                                            : "Select Work Order First"}
                                                    </option>

                                                    {mirs.map((mir) => (
                                                        <option key={mir._id} value={mir._id}>
                                                            {mir.mirNumber}
                                                        </option>
                                                    ))}
                                                </Form.Select>

                                                <Button
                                                    variant="outline-primary"
                                                    onClick={handleAddMir}
                                                    disabled={!selectedMirId}
                                                >
                                                    Add
                                                </Button>
                                            </div>

                                            {selectedMirs.length > 0 && (
                                                <div className="mt-2">
                                                    {selectedMirs.map((mir) => (
                                                        <Badge
                                                            key={mir.mirId}
                                                            bg="secondary"
                                                            className="me-2 mb-2"
                                                            style={{
                                                                fontSize: "14px",
                                                                padding: "8px 12px",
                                                            }}
                                                        >
                                                            {mir.mirNumber}
                                                            <span
                                                                style={{
                                                                    cursor: "pointer",
                                                                    marginLeft: "8px",
                                                                }}
                                                                onClick={() => handleRemoveMir(mir.mirId)}
                                                            >
                                                                x
                                                            </span>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Supporting Documents</Form.Label>

                                            <Alert variant="info" className="fs-12">
                                                Upload supporting documents like JMR copies, approved
                                                drawings, material inspection records, test reports,
                                                checklists, photographs, etc.
                                            </Alert>

                                            <Row className="g-2">
                                                <Col md={3}>
                                                    <Form.Label className="fs-12">JMR Document</Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        size="sm"
                                                        onChange={(e) => handleFileChange(e, "JMR")}
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label className="fs-12">
                                                        Material Inspection
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        size="sm"
                                                        onChange={(e) =>
                                                            handleFileChange(e, "MaterialInspection")
                                                        }
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label className="fs-12">Test Report</Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        size="sm"
                                                        onChange={(e) =>
                                                            handleFileChange(e, "TestReport")
                                                        }
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label className="fs-12">Checklist</Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        size="sm"
                                                        onChange={(e) =>
                                                            handleFileChange(e, "Checklist")
                                                        }
                                                    />
                                                </Col>

                                                <Col md={3}>
                                                    <Form.Label className="fs-12">Photograph</Form.Label>
                                                    <Form.Control
                                                        type="file"
                                                        size="sm"
                                                        onChange={(e) =>
                                                            handleFileChange(e, "Photograph")
                                                        }
                                                    />
                                                </Col>
                                            </Row>

                                            {supportingDocs.length > 0 && (
                                                <div className="mt-2">
                                                    <Table size="sm" bordered>
                                                        <thead>
                                                            <tr>
                                                                <th>Type</th>
                                                                <th>File Name</th>
                                                                <th>Action</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {supportingDocs.map((doc, index) => (
                                                                <tr key={`${doc.documentType}-${index}`}>
                                                                    <td>{doc.documentType}</td>
                                                                    <td><WirDocumentPreview doc={doc} /></td>
                                                                    <td>
                                                                        <Button
                                                                            variant="danger"
                                                                            size="sm"
                                                                            onClick={() => handleRemoveDoc(index)}
                                                                        >
                                                                            Remove
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </Table>
                                                </div>
                                            )}
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Inspection Remarks</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="inspectionRemarks"
                                                value={formData.inspectionRemarks}
                                                onChange={handleChange}
                                                placeholder="Enter inspection remarks or observations"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>General Remarks</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="remarks"
                                                value={formData.remarks}
                                                onChange={handleChange}
                                                placeholder="Enter any additional remarks"
                                            />
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <h5 className="mb-3">Corrective Action & Re-inspection</h5>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Corrective Action Required</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={3}
                                                name="correctiveAction"
                                                value={formData.correctiveAction}
                                                onChange={handleChange}
                                                placeholder="Enter corrective actions required"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Label>Re-inspection Required?</Form.Label>
                                            <Form.Select
                                                name="reinspectionRequired"
                                                value={formData.reinspectionRequired}
                                                onChange={handleChange}
                                            >
                                                <option value="false">No</option>
                                                <option value="true">Yes</option>
                                            </Form.Select>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Re-inspection Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="reinspectionDate"
                                                value={formData.reinspectionDate}
                                                onChange={handleChange}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>New WIR No. (if raised)</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="newWirNo"
                                                value={formData.newWirNo}
                                                onChange={handleChange}
                                                placeholder="Enter new WIR number"
                                            />
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <h5 className="mb-3">Inspector Details</h5>

                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Label>Inspector Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="inspectorName"
                                                value={formData.inspectorName}
                                                onChange={handleChange}
                                                placeholder="Enter inspector name"
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Designation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="inspectorDesignation"
                                                value={formData.inspectorDesignation}
                                                onChange={handleChange}
                                                placeholder="Enter designation"
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Organisation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="inspectorOrganisation"
                                                value={formData.inspectorOrganisation}
                                                onChange={handleChange}
                                                placeholder="Enter organisation"
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Label>Inspector Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="inspectorDate"
                                                value={formData.inspectorDate}
                                                onChange={handleChange}
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Inspector Signature</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="inspectorSignature"
                                                onChange={(e) => handleFileChange(e, "inspectorSignature")}
                                            />
                                            {renderSelectedDocPreview("inspectorSignature")}
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Inspector Stamp</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="inspectorStamp"
                                                onChange={(e) => handleFileChange(e, "inspectorStamp")}
                                            />
                                            {renderSelectedDocPreview("inspectorStamp")}
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    <h5 className="mb-3">Client / Owner Approval (Final)</h5>

                                    <Row className="mb-3">
                                        <Col md={12}>
                                            <Form.Label>Client Decision</Form.Label>
                                            <Form.Select
                                                name="clientDecision"
                                                value={formData.clientDecision}
                                                onChange={handleChange}
                                            >
                                                <option value="">Select Decision</option>
                                                <option value="APPROVED - No further action required">
                                                    APPROVED - No further action required
                                                </option>
                                                <option value="APPROVED WITH CONDITIONS - Conditions noted overleaf">
                                                    APPROVED WITH CONDITIONS - Conditions noted overleaf
                                                </option>
                                                <option value="NOT APPROVED - Refer back to Contractor">
                                                    NOT APPROVED - Refer back to Contractor
                                                </option>
                                            </Form.Select>
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Label>Client Representative Name</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="clientRepresentativeName"
                                                value={formData.clientRepresentativeName}
                                                onChange={handleChange}
                                                placeholder="Enter representative name"
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Designation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="clientDesignation"
                                                value={formData.clientDesignation}
                                                onChange={handleChange}
                                                placeholder="Enter designation"
                                            />
                                        </Col>

                                        <Col md={4}>
                                            <Form.Label>Client Date</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="clientDate"
                                                value={formData.clientDate}
                                                onChange={handleChange}
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mb-3">
                                        <Col md={6}>
                                            <Form.Label>Client Signature</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="clientSignature"
                                                onChange={(e) => handleFileChange(e, "clientSignature")}
                                            />
                                            {renderSelectedDocPreview("clientSignature")}
                                        </Col>

                                        <Col md={6}>
                                            <Form.Label>Client Stamp/Seal</Form.Label>
                                            <Form.Control
                                                type="file"
                                                name="clientStamp"
                                                onChange={(e) => handleFileChange(e, "clientStamp")}
                                            />
                                            {renderSelectedDocPreview("clientStamp")}
                                        </Col>
                                    </Row>

                                    <div className="d-flex gap-2 justify-content-end">
                                        <Link to="/wir" className="btn btn-outline-secondary">
                                            Cancel
                                        </Link>

                                        <Button
                                            type="button"
                                            variant="primary"
                                            onClick={() => handleSubmit("Draft")}
                                            disabled={submitting}
                                        >
                                            {submittingAction === "Draft" ? <Spinner size="sm" /> : "Save as Draft"}
                                        </Button>

                                        <Button
                                            type="button"
                                            variant="success"
                                            onClick={() => handleSubmit("Submitted")}
                                            disabled={submitting}
                                        >
                                            {submittingAction === "Submitted" ? (
                                                <Spinner size="sm" />
                                            ) : (
                                                "Submit for Inspection"
                                            )}
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};

export default WirAdd;
